import { GamoraBase64URLGuard, assert } from '@ver.id/libs.internal.gamora';

export type CompactJWS = {
  payload: unknown;
  header: { alg: string; typ: string; kid: string };
  signature: string;
};

const CompactRegExp =
  /^(?<header>[a-zA-Z0-9\-_]+)\.(?<payload>[a-zA-Z0-9\-_]+)\.(?<signature>[a-zA-Z0-9\-_]+)$/;

export const fromCompact = <T extends CompactJWS>(compact: string): T => {
  assert(
    typeof compact === 'string' && compact.length > 0,
    'Invalid input: not a valid JWS compact string.',
  );

  const jwsMatches = CompactRegExp.exec(compact);
  assert(
    jwsMatches !== null && jwsMatches.length !== 0,
    'Invalid input: not a valid JWS.',
  );

  const base64URLHeader = GamoraBase64URLGuard.fromBase64URL(
    jwsMatches?.groups?.header,
  );
  const base64URLPayload = GamoraBase64URLGuard.fromBase64URL(
    jwsMatches?.groups?.payload,
  );
  const base64URLSignature = GamoraBase64URLGuard.fromBase64URL(
    jwsMatches?.groups?.signature,
  );

  const jsonHeader = JSON.parse(base64URLHeader.toUTF8());
  const jsonPayload = JSON.parse(base64URLPayload.toUTF8());

  assert(
    typeof jsonHeader === 'object' && jsonHeader !== null,
    'Invalid jsonHeader: not an object.',
  );

  const jws: CompactJWS = {
    header: {
      alg: jsonHeader?.alg,
      typ: jsonHeader?.typ,
      kid: jsonHeader?.kid,
    },
    payload: jsonPayload,
    signature: base64URLSignature.toBase64URL(),
  };

  return jws as T;
};

export const toCompact = <T extends CompactJWS>(jws: T): string => {
  const jsonHeader = JSON.stringify(jws.header);
  const jsonPayload = JSON.stringify(jws.payload);

  const base64URLHeader = GamoraBase64URLGuard.fromUTF8(jsonHeader);
  const base64URLPayload = GamoraBase64URLGuard.fromUTF8(jsonPayload);
  const base64URLSignature = GamoraBase64URLGuard.fromBase64URL(jws.signature);

  return (
    base64URLHeader.toBase64URL() +
    '.' +
    base64URLPayload.toBase64URL() +
    '.' +
    base64URLSignature.toBase64URL()
  );
};
